import { Text, get, Flex, Link, Box, Heading, Badge } from 'theme-ui';
import { format } from 'date-fns';
import { Company } from '../containers/companyDetails/types';
import {
    GlobeIcon,
    TrashIcon,
    ServerIcon,
    BellIcon,
    DocumentAddIcon,
    TemplateIcon,
    CreditCardIcon,
    CollectionIcon,
    CodeIcon,
    ClockIcon,
    CheckCircleIcon,
} from '@heroicons/react/outline';
import { LoginAsAdmin } from '@/containers/companies/LoginAsAdmin';
import { setCompanyId } from '@/store/deleteCompany';
import { setMarkCompanyId } from '@/store/markForDeletion';
import { useDispatch } from 'react-redux';
import { useGetCompanyDeletionSchedulesQuery } from '@/store/apiService';
import { COMPANY_DELETION_SCHEDULE_STATUS, COMPANY_STATUS } from '@/types/types';
import { ApproveCompanyModal } from '@/containers/approveCompanyModal/ApproveCompanyModal';
import { useState } from 'react';

const APPROVABLE_STATUSES: number[] = [
    COMPANY_STATUS.Inactive,
    COMPANY_STATUS.Closed,
    COMPANY_STATUS.NotApproved,
];

export const CompanyHeader = ({
    onApproved,
    ...company
}: Company & { onApproved?: () => void }) => {
    const dispatch = useDispatch();
    const [approveVisible, setApproveVisible] = useState(false);
    const canApprove = APPROVABLE_STATUSES.includes(company.StatusId ?? 0);
    const deletionSchedules = useGetCompanyDeletionSchedulesQuery(
        { CompanyId: company.Id, Status: COMPANY_DELETION_SCHEDULE_STATUS.Scheduled },
        { skip: !company.Id }
    );
    const scheduledDeletion = deletionSchedules.data?.Results?.[0];

    return (
        <Box mb="3">
            <Flex>
                <Box
                    mr="3"
                    sx={{
                        width: '80px',
                        height: '80px',
                        borderRadius: 'xsm',
                        backgroundImage: `url('${company.LogoType}')`,
                        backgroundPosition: 'top top',
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        border: (t) => `1px solid ${get(t, 'colors.gray.2')}`,
                    }}
                />
                <Box>
                    <Heading>{company.Name}</Heading>
                    <Flex sx={{ gap: 2, alignItems: 'center' }}>
                        {company.Active ? (
                            <Badge mb="2" variant="active">
                                Active
                            </Badge>
                        ) : null}
                        {scheduledDeletion ? (
                            <Badge mb="2" backgroundColor="danger">
                                Deletion scheduled
                                {scheduledDeletion.ScheduledDeletionDate
                                    ? ` ${format(
                                          new Date(scheduledDeletion.ScheduledDeletionDate),
                                          'd MMM yyyy'
                                      )}`
                                    : ''}
                            </Badge>
                        ) : null}
                    </Flex>
                    <Flex sx={{ flexWrap: 'wrap', gap: 2 }}>
                        <LoginAsAdmin companyId={company.Id} variant="header" />
                        <Link
                            variant="header"
                            target="_blank"
                            href={`https://${company?.SitePath}.${process.env.REACT_APP_COMPANY_HOMEPAGE_DOMAIN_NAME}`}
                        >
                            <GlobeIcon width={16} height={16} />
                            <Text>Navigate to website</Text>
                        </Link>
                        <Link variant="header" href={`/#/apikeys/${company.Id}`}>
                            <ServerIcon width={16} height={16} />
                            <Text>Create API key</Text>
                        </Link>
                        <Link variant="header" href={`/#/clients/${company.Id}/incentives/new`}>
                            <BellIcon width={16} height={16} />
                            <Text>Create incentive</Text>
                        </Link>
                        <Link variant="header" href={`/#/clients/${company.Id}/createusage`}>
                            <DocumentAddIcon width={16} height={16} />
                            <Text>Create usage</Text>
                        </Link>
                        <Link
                            variant="header"
                            href={`/#/clients/${company.Id}/widget/configurations`}
                        >
                            <TemplateIcon width={16} height={16} />
                            <Text>Widget configurations</Text>
                        </Link>
                        <Link variant="header" href={`/#/clients/${company.Id}/billing`}>
                            <CreditCardIcon width={16} height={16} />
                            <Text>Edit billing information</Text>
                        </Link>
                        <Link variant="header" href={`/#/clients/${company.Id}/services-articles`}>
                            <CollectionIcon width={16} height={16} />
                            <Text>Services & articles</Text>
                        </Link>
                        <Link variant="header" href={`/#/clients/${company.Id}/embed`}>
                            <CodeIcon width={16} height={16} />
                            <Text>Embed & preview</Text>
                        </Link>
                        {canApprove ? (
                            <Link
                                variant="header"
                                href={`#`}
                                onClick={(evt) => {
                                    evt.preventDefault();
                                    setApproveVisible(true);
                                }}
                            >
                                <CheckCircleIcon width={16} height={16} />
                                <Text>Mark as approved</Text>
                            </Link>
                        ) : null}
                        {!scheduledDeletion ? (
                            <Link
                                variant="header"
                                href={`#`}
                                color="danger"
                                onClick={(evt) => {
                                    evt.preventDefault();
                                    dispatch(
                                        setMarkCompanyId({
                                            companyId: company.Id,
                                            inactivityNotificationDate:
                                                company.InactivityNotificationDate,
                                        })
                                    );
                                }}
                            >
                                <ClockIcon width={16} height={16} color="danger" />
                                <Text color="danger">Mark for deletion</Text>
                            </Link>
                        ) : null}
                        <Link
                            variant="header"
                            href={`#`}
                            color="danger"
                            onClick={(evt) => {
                                evt.preventDefault();
                                dispatch(setCompanyId(company.Id));
                            }}
                        >
                            <TrashIcon width={16} height={16} color="danger" />
                            <Text color="danger">Delete</Text>
                        </Link>
                    </Flex>
                </Box>
            </Flex>
            {approveVisible ? (
                <ApproveCompanyModal
                    companyId={company.Id as string}
                    hasScheduledDeletion={!!scheduledDeletion}
                    onClose={() => setApproveVisible(false)}
                    onApproved={onApproved}
                />
            ) : null}
        </Box>
    );
};
